import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ApiClient from "../../apis/Api-client";
import { END_POINT } from "../../utils/End-points";
import { Helper } from "../../utils/Helpers";
import { successToster, errorToster } from "../../shared/toster/Toster";

const { roundOff } = new Helper();

const REFUND_REASONS = [
  { value: "EXCESS_PAYMENT", label: "Excess Payment" },
  { value: "RESERVATION_CANCELLED", label: "Reservation Cancelled" },
  { value: "CHECKIN_DELETED", label: "Check-in Deleted" },
  { value: "CHARGE_REVERSAL", label: "Charge Reversal" },
  { value: "PRICE_ADJUSTMENT", label: "Price Adjustment" },
  { value: "GUEST_REQUEST", label: "Guest Request" },
  { value: "OTHER", label: "Other" },
];

const RefundDialog = ({
  open,
  onClose,
  bookingId,
  maxRefundable = 0,
  defaultReason = "EXCESS_PAYMENT",
  defaultAmount = 0,
  originalPaymentId = null,
  invoiceId = null,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    refund_amount: defaultAmount || "",
    refund_payment_type_id: "",
    refund_reason: defaultReason,
    refund_remarks: "",
  });
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      fetchPaymentTypes();
      setFormData({
        refund_amount: defaultAmount || "",
        refund_payment_type_id: "",
        refund_reason: defaultReason,
        refund_remarks: "",
      });
      setError("");
    }
  }, [open, defaultAmount, defaultReason]);

  const fetchPaymentTypes = async () => {
    try {
      const { api } = ApiClient();
      const res = await api.get(END_POINT.PAYMENT.GET_ALL_PAYMENT_TYPE);
      if (res?.data?.data) {
        setPaymentTypes(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch payment types", err);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError("");
  };

  const validate = () => {
    const amount = Number(formData.refund_amount);
    if (!bookingId) {
      setError("Booking ID is missing");
      return false;
    }
    if (!amount || amount <= 0) {
      setError("Refund amount must be greater than 0");
      return false;
    }
    if (amount - Number(maxRefundable) > 0.005) {
      setError(`Refund amount cannot exceed ${roundOff(maxRefundable)}`);
      return false;
    }
    if (!formData.refund_payment_type_id) {
      setError("Please select a refund payment method");
      return false;
    }
    if (!formData.refund_reason) {
      setError("Please select a refund reason");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const { api } = ApiClient();
      const payload = {
        booking_id: bookingId,
        refund_amount: Number(Number(formData.refund_amount).toFixed(2)),
        refund_payment_type_id: Number(formData.refund_payment_type_id),
        refund_reason: formData.refund_reason,
        refund_remarks: formData.refund_remarks || null,
        original_payment_id: originalPaymentId,
        invoice_id: invoiceId,
      };

      const res = await api.post(END_POINT.REFUND.CREATE, payload);
      if (res?.data?.__approvalPending) {
        onClose();
      } else if (res?.data?.status !== false) {
        successToster("Refund created successfully");
        onSuccess?.(res?.data?.data);
        onClose();
      } else {
        errorToster(res?.data?.msg || "Failed to create refund");
      }
    } catch (err) {
      if (!err?.approvalPending) {
        errorToster(err?.response?.data?.msg || "Failed to create refund");
      } else {
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.3)" } },
      }}
      PaperProps={{
        sx: { borderRadius: "12px" },
      }}
    >
      <DialogTitle>Issue Refund</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Maximum Refundable: <strong>{roundOff(maxRefundable)}</strong>
          </Typography>

          <TextField
            label="Refund Amount"
            value={formData.refund_amount}
            onChange={(e) => {
              const val = e.target.value
                .replace(/[^0-9.]/g, "")
                .replace(/(\..*)\./g, "$1")
                .replace(/^(\d*\.\d{2}).+$/, "$1");
              handleChange("refund_amount", val);
            }}
            onKeyDown={(e) => {
              if (["e", "E", "+", "-", " "].includes(e.key)) {
                e.preventDefault();
              }
            }}
            fullWidth
            size="small"
            inputProps={{ inputMode: "numeric" }}
          />

          <TextField
            label="Refund Method"
            select
            value={formData.refund_payment_type_id}
            onChange={(e) => handleChange("refund_payment_type_id", e.target.value)}
            fullWidth
            size="small"
          >
            {paymentTypes.map((pt) => (
              <MenuItem key={pt.payment_type_id} value={pt.payment_type_id}>
                {pt.payment_type}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Refund Reason"
            select
            value={formData.refund_reason}
            onChange={(e) => handleChange("refund_reason", e.target.value)}
            fullWidth
            size="small"
          >
            {REFUND_REASONS.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Remarks (Optional)"
            value={formData.refund_remarks}
            onChange={(e) => handleChange("refund_remarks", e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={2}
            inputProps={{ maxLength: 255 }}
          />

          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? "Processing..." : "Confirm Refund"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RefundDialog;
